import React from "react";
import SearchHighlighter from "../../../components/dashboard/common/Documentation/SearchHighlighter";

const Introduction = () => {
  return (
    <SearchHighlighter>
      <div className="mb-12">
        <div className="flex items-center gap-3 text-sm font-bold text-[#0070f3] uppercase tracking-widest mb-4">
          <span>Capture The Bug</span>
          <span className="text-slate-300">|</span>
          <span>Continuous PTaaS Platform</span>
        </div>
        <h1 className="text-5xl font-extrabold tracking-tighter text-black m-0 mb-6">
          Enterprise Onboarding Guide
        </h1>
        <div className="h-px w-full bg-[#eaeaea]" />
      </div>

      <div id="overview" className="scroll-mt-24">
        <h2 className="text-2xl font-bold text-black mb-4">Overview</h2>
        <p>
          This guide provides a complete, field-level walkthrough of the Capture The Bug (CTB) Continuous Penetration Testing as a Service (PTaaS) onboarding flow. Following these steps in order will bring your organization from zero state to full attack surface coverage with active researcher engagement.
        </p>
        <div className="mt-6 p-6 rounded-xl bg-blue-50/50 border border-blue-100 italic text-blue-900">
          <b>Goal:</b> Move from zero-state to 100% attack surface coverage and active researcher engagement in less than 24 hours.
        </div>

        <h2 id="interactive-tour" className="text-2xl font-bold text-black mt-16 mb-6">Interactive Platform Tour</h2>
        <p className="mb-8 text-slate-600">Experience the CTB onboarding flow in real-time. This interactive guide walks you through asset registration, target scoping, and program deployment.</p>
        
        <div 
          style={{ 
            position: 'relative', 
            boxSizing: 'content-box', 
            maxHeight: '80vh', 
            width: '100%', 
            aspectRatio: '1.90', 
            padding: '40px 0' 
          }}
          className="mb-12"
        >
          <iframe 
            src="https://app.supademo.com/embed/cmn5lp1rk3hxqz3qm2y3mw7et?embed_v=2&utm_source=embed" 
            loading="lazy" 
            title="Localhost Demo" 
            allow="clipboard-write" 
            frameBorder="0" 
            allowFullScreen 
            style={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              width: '100%', 
              height: '100%' 
            }}
          />
        </div>
        
        <h3 className="text-xl font-bold text-black mt-10 mb-4">What You Will Complete</h3>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 list-none p-0 m-0">
          <li className="flex items-start gap-2 text-slate-600 before:content-['✓'] before:text-emerald-500 before:font-bold">Register and classify digital assets in the Asset Inventory</li>
          <li className="flex items-start gap-2 text-slate-600 before:content-['✓'] before:text-emerald-500 before:font-bold">Define technical targets and testing scope</li>
          <li className="flex items-start gap-2 text-slate-600 before:content-['✓'] before:text-emerald-500 before:font-bold">Discover and catalog endpoints within your attack surface</li>
          <li className="flex items-start gap-2 text-slate-600 before:content-['✓'] before:text-emerald-500 before:font-bold">Store credentials securely in the Credential Vault</li>
          <li className="flex items-start gap-2 text-slate-600 before:content-['✓'] before:text-emerald-500 before:font-bold">Configure and deploy a testing program</li>
          <li className="flex items-start gap-2 text-slate-600 before:content-['✓'] before:text-emerald-500 before:font-bold">Schedule and manage testing runs</li>
          <li className="flex items-start gap-2 text-slate-600 before:content-['✓'] before:text-emerald-500 before:font-bold">Submit and verify the run snapshot</li>
          <li className="flex items-start gap-2 text-slate-600 before:content-['✓'] before:text-emerald-500 before:font-bold">Access support and compliance artifacts</li>
        </ul>
      </div>

      <div id="platform-tiers" className="mt-16 scroll-mt-24">
        <h2 className="text-2xl font-bold text-black mb-6">Platform Tiers</h2>
        <div className="overflow-x-auto border border-[#eaeaea] rounded-lg">
          <table className="min-w-full divide-y divide-[#eaeaea] m-0">
            <thead className="bg-[#fafafa]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-[#eaeaea]">Tier</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-[#eaeaea]">Schedule Type</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-black uppercase tracking-wider">Key Capability</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#eaeaea]">
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-black border-r border-[#eaeaea]">Enterprise</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 border-r border-[#eaeaea]">Continuous / Recurring</td>
                <td className="px-6 py-4 text-sm text-slate-600">Recurring cycles with unit quota management</td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-black border-r border-[#eaeaea]">One-off / Pilot</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 border-r border-[#eaeaea]">Fixed-window Engagement</td>
                <td className="px-6 py-4 text-sm text-slate-600">Single engagement with defined start and end dates</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </SearchHighlighter>
  );
};

export default Introduction;
